import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { StatsBand } from "@/components/StatsBand";
import { FAQSection } from "@/components/FAQSection";
import { iconFor } from "@/lib/iconMap";
import DATA from "@/data/verticalsData.json";

const Reveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const reduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      setOn(true);
      return undefined;
    }
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && (setOn(true), obs.disconnect())),
      { threshold: 0.15 }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className={`${className} transition-all duration-700 ease-out ${on ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

const Card = ({ icon, title, line, testId }) => {
  const Icon = iconFor(icon);
  return (
    <div
      data-testid={testId}
      className="h-full rounded-[12px] border border-[#ececec] bg-white p-6 shadow-[0_6px_18px_rgba(2,2,2,0.04)] hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(2,2,2,0.1)] hover:border-[#ee2e24]/30 transition-transform duration-200"
    >
      <div className="h-11 w-11 rounded-[10px] bg-[#ee2e24]/[0.08] flex items-center justify-center">
        <Icon size={20} strokeWidth={1.75} className="text-[#ee2e24]" />
      </div>
      <h3 className="mt-4 font-nunito text-[15px] font-extrabold text-[#383838]">{title}</h3>
      <p className="mt-2 text-[13px] text-[#6e6e6e] leading-relaxed">{line}</p>
    </div>
  );
};

export default function IndustryPage({ vkey }) {
  const V = DATA.verticals[vkey];
  const process = DATA.process;
  if (!V) return null;
  return (
    <div data-testid={`industry-page-${vkey}`} className="bg-white">
      <SiteHeader />
      <main>
        {/* Hero */}
        <section data-testid="industry-hero" className="relative overflow-hidden bg-[#0b0b0c] hero-noise">
          <img src={V.images.hero} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div
            className="absolute inset-0"
            style={{ background: "linear-gradient(90deg, rgba(2,2,2,0.86) 0%, rgba(2,2,2,0.62) 50%, rgba(2,2,2,0.86) 100%)" }}
          />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 min-h-[520px] md:min-h-[560px] flex items-center justify-center pt-32 pb-16 md:pt-36 md:pb-20">
            <div className="text-center max-w-3xl mx-auto">
              <p className="font-nunito text-[11px] sm:text-xs font-extrabold uppercase tracking-[0.3em] text-[#ff6b63] mb-5">
                Industry Vertical · {V.name}
              </p>
              <h1
                data-testid="industry-hero-title"
                className="font-nunito font-extrabold text-white text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.08] tracking-tight"
              >
                {V.heroTitlePre}
                <span className="text-[#ee2e24]">Marketing Machine™</span>
              </h1>
              <p className="mt-5 text-[#dedede] text-sm sm:text-base md:text-lg leading-relaxed max-w-2xl mx-auto">{V.heroSub}</p>
              <div className="mt-8">
                <Link
                  to="/#visibility-audit"
                  data-testid="industry-hero-cta"
                  className="inline-flex justify-center items-center rounded-[10px] bg-[#ee2e24] px-7 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-white shadow-[0_14px_40px_rgba(238,46,36,0.3)] hover:bg-[#d92820] active:scale-[0.98] transition-colors duration-150"
                >
                  Get My Free Visibility Audit
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Pain points */}
        <section data-testid="industry-pains" className="bg-white py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <h2 className="font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838] heading-redul max-w-3xl">
              Three shifts deciding who wins your market.
            </h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {V.pains.map((item, i) => (
                <Reveal key={item.title} delay={i * 80}>
                  <Card {...item} testId={`industry-pain-card-${i + 1}`} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Story photo split */}
        <section data-testid="industry-story" className="bg-[#fafafa] border-y border-[#f1f1f1] py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <Reveal>
              <img
                src={V.images.story}
                alt={`${V.name} marketing`}
                className="w-full rounded-[14px] shadow-[0_18px_50px_rgba(2,2,2,0.18)]"
                data-testid="industry-story-photo"
              />
            </Reveal>
            <div>
              <p className="font-nunito text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#ee2e24]">
                Why {V.name.toLowerCase()} is different
              </p>
              <h2 className="mt-3 font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838] heading-redul">
                {V.story.heading}
              </h2>
              <p className="mt-5 text-[15px] text-[#6e6e6e] leading-relaxed">{V.story.body}</p>
              <Link
                to="/#visibility-audit"
                data-testid="industry-story-cta"
                className="mt-7 inline-flex justify-center items-center rounded-[10px] bg-[#ee2e24] px-6 py-3 font-nunito text-[12px] font-extrabold uppercase tracking-[0.12em] text-white hover:bg-[#d92820] transition-colors duration-150"
              >
                Get My Free Visibility Audit
              </Link>
            </div>
          </div>
        </section>

        {/* Machine tuned */}
        <section data-testid="industry-machine" className="bg-white py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto">
              <p className="font-nunito text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#ee2e24]">
                The machine, tuned for {V.name.toLowerCase()}
              </p>
              <h2 className="mt-3 font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838] heading-redul-center">
                Four engines calibrated to how your customers actually choose.
              </h2>
            </div>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {V.machine.map((item, i) => (
                <Reveal key={item.title} delay={i * 80}>
                  <Card {...item} testId={`industry-machine-card-${i + 1}`} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* Segments */}
        <section data-testid="industry-segments" className="bg-[#fafafa] border-y border-[#f1f1f1] py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-2xl mx-auto">
              <p className="font-nunito text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#ee2e24]">{V.segHeading}</p>
              <h2 className="mt-3 font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838] heading-redul-center">
                One machine. Every segment.
              </h2>
            </div>
            <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              {V.segments.map((item, i) => {
                const Icon = iconFor(item.icon);
                return (
                  <Reveal key={item.name} delay={i * 60}>
                    <div
                      data-testid={`industry-segment-card-${i + 1}`}
                      className="h-full rounded-[12px] border border-[#ececec] bg-white px-3.5 py-6 text-center shadow-[0_6px_18px_rgba(2,2,2,0.04)] hover:-translate-y-1 hover:shadow-[0_14px_40px_rgba(2,2,2,0.1)] hover:border-[#ee2e24]/30 transition-transform duration-200"
                    >
                      <span className="mx-auto h-11 w-11 rounded-full bg-[#ee2e24]/[0.08] flex items-center justify-center">
                        <Icon size={19} strokeWidth={1.75} className="text-[#ee2e24]" />
                      </span>
                      <h5 className="mt-3 font-nunito text-[13px] font-extrabold text-[#383838]">{item.name}</h5>
                      <p className="mt-1.5 text-[11.5px] text-[#8a8a8a] leading-snug">{item.line}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* Process */}
        <section data-testid="industry-process" className="bg-[#0b0b0c] py-16 md:py-20">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-[2fr_3fr] gap-10">
            <div>
              <p className="font-nunito text-[11px] font-extrabold uppercase tracking-[0.3em] text-[#ff6b63]">How it works</p>
              <h2 className="mt-3 font-nunito text-2xl sm:text-3xl font-extrabold text-white">
                From invisible to inevitable — in three moves.
              </h2>
            </div>
            <div className="space-y-4">
              {process.map((s, i) => {
                const Icon = iconFor(s.icon);
                return (
                  <Reveal key={s.title} delay={i * 100}>
                    <div data-testid={`industry-process-step-${i + 1}`} className="rounded-[14px] border border-white/10 bg-white/[0.05] p-6">
                      <div className="flex items-center gap-3">
                        <span className="h-10 w-10 rounded-full border border-white/20 bg-white/[0.06] flex items-center justify-center">
                          <Icon size={17} strokeWidth={1.75} className="text-[#ee2e24]" />
                        </span>
                        <h3 className="font-nunito text-sm font-extrabold uppercase tracking-[0.22em] text-white">{s.title}</h3>
                      </div>
                      <p className="mt-4 text-[14px] text-[#ececec] leading-relaxed">{s.body}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        <StatsBand />

        <FAQSection heading={`${V.name} marketing questions, answered`} items={V.faq.map((f) => ({ q: f.q, a: f.a }))} injectSchema />

        {/* CTA */}
        <section data-testid="industry-cta" className="bg-[#fafafa] border-t border-[#f1f1f1] py-16 md:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="font-nunito text-2xl sm:text-3xl font-extrabold text-[#383838]">{V.ctaHeading}</h2>
            <p className="mt-4 text-[15px] text-[#6e6e6e] leading-relaxed">
              Free Visibility Audit — your rankings, AI citations and paid efficiency versus the competitors you actually lose to. From a US-based strategist, in 2 business days or less.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
              <Link
                to="/#visibility-audit"
                data-testid="industry-cta-primary-button"
                className="w-full sm:w-auto inline-flex justify-center items-center rounded-[10px] bg-[#ee2e24] px-7 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-white shadow-[0_14px_40px_rgba(238,46,36,0.3)] hover:bg-[#d92820] active:scale-[0.98] transition-colors duration-150"
              >
                Get My Free Visibility Audit
              </Link>
              <Link
                to="/contact"
                data-testid="industry-cta-secondary-button"
                className="w-full sm:w-auto inline-flex justify-center items-center rounded-[10px] border border-[#d9d9d9] px-7 py-3.5 font-nunito text-[13px] font-extrabold uppercase tracking-[0.12em] text-[#383838] hover:border-[#ee2e24] hover:text-[#ee2e24] transition-colors duration-150"
              >
                Talk to a Strategist
              </Link>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
